// Apolo IDE — ComposerAtMenu (Cursor-style "@" context menu).
//
// Shows on a BARE `@` in the composer (detectAtMenuPrefix). Two sections:
//   1. CONTEXTO — os arquivos que o usuário adicionou no painel Contexto
//      (imagens/docs). Picar um ANEXA ele à mensagem atual (é enviado no turno).
//      Antes o `@` ignorava esses arquivos ("@ não aciona o que está no Contexto").
//   2. Ações — Plan / Multitask / Ask / Image / Skills / Files (o @file: typeahead).
// Type after the @ to filter AMBAS as seções. Pick → onSelect(id) roda a ação
// mapeada (ou `ctx:<attId>` pra anexar o arquivo de contexto) e o composer strips
// o @token. Keyboard: ↑/↓ movem pela lista combinada, Enter pica, Esc dispensa —
// em CAPTURE na window (nunca chega no textarea → sem submit acidental).
//
// Dark Cursor-IDE styling (.rx-atmenu em redesign.css): flat, hairline, sem glow.

import { useEffect, useMemo, useState } from 'react';
import {
  ClipboardList, Users, MessageCircleQuestion, Image as ImageIcon,
  Sparkles, FileText, File as FileIcon, Film, Music,
} from 'lucide-react';
import { detectAtMenuPrefix, filterAtMenuItems } from '../../lib/atMenu.js';

const ICON = {
  plan: ClipboardList,
  multitask: Users,
  ask: MessageCircleQuestion,
  image: ImageIcon,
  skills: Sparkles,
  files: FileText,
};

// Ícone por tipo do arquivo de contexto (espelha o ContextTabView).
function ctxIcon(kind, name) {
  if (kind === 'image') return ImageIcon;
  if (kind === 'text') return FileText;
  const n = (name || '').toLowerCase();
  if (/\.(mp4|webm|mov|m4v|avi|mkv)$/.test(n)) return Film;
  if (/\.(mp3|wav|ogg|m4a|flac|aac)$/.test(n)) return Music;
  return FileIcon;
}

// Filtra os arquivos de contexto pelo texto após o @ (substring no nome).
function filterContextItems(contextItems, query) {
  const list = Array.isArray(contextItems) ? contextItems : [];
  const q = String(query || '').trim().toLowerCase();
  if (!q) return list;
  return list.filter((it) => (it.name || '').toLowerCase().includes(q));
}

export default function ComposerAtMenu({ input, contextItems = [], onSelect, onDismiss }) {
  const detected = useMemo(() => detectAtMenuPrefix(input), [input]);
  const ctx = useMemo(
    () => (detected ? filterContextItems(contextItems, detected.query) : []),
    [detected, contextItems],
  );
  const actions = useMemo(
    () => (detected ? filterAtMenuItems(detected.query) : []),
    [detected],
  );
  // Lista combinada (contexto primeiro) — a navegação por teclado é sobre ela.
  const items = useMemo(
    () => [
      ...ctx.map((c) => ({ id: `ctx:${c.id}`, label: c.name, kind: c.kind, isCtx: true })),
      ...actions.map((a) => ({ ...a, isCtx: false })),
    ],
    [ctx, actions],
  );
  const [active, setActive] = useState(0);
  const visible = !!detected && items.length > 0;

  useEffect(() => { setActive(0); }, [detected?.query]);

  useEffect(() => {
    if (!visible) return undefined;
    function onKey(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault(); e.stopPropagation();
        setActive((a) => (a + 1) % items.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault(); e.stopPropagation();
        setActive((a) => (a - 1 + items.length) % items.length);
      } else if (e.key === 'Enter') {
        e.preventDefault(); e.stopPropagation();
        onSelect?.(items[active]?.id, detected);
      } else if (e.key === 'Escape') {
        e.stopPropagation();
        onDismiss?.();
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [visible, items, active, detected, onSelect, onDismiss]);

  if (!visible) return null;

  const firstActionIdx = ctx.length; // onde começa a seção de ações

  return (
    <div className="rx-atmenu" role="listbox" aria-label="Adicionar contexto, agentes, ferramentas">
      {ctx.length > 0 && <div className="rx-atmenu-h">Contexto · {ctx.length}</div>}
      {items.map((it, i) => {
        const Icon = it.isCtx ? ctxIcon(it.kind, it.label) : (ICON[it.id] || FileText);
        return (
          <div key={it.id}>
            {!it.isCtx && i === firstActionIdx && (
              <div className="rx-atmenu-h">Adicionar agentes, ferramentas…</div>
            )}
            <button
              type="button"
              role="option"
              aria-selected={i === active}
              data-testid={it.isCtx ? `atmenu-ctx` : `atmenu-${it.id}`}
              className={`rx-atmenu-item${i === active ? ' active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => e.preventDefault()} // keep textarea focus
              onClick={() => onSelect?.(it.id, detected)}
            >
              <Icon size={14} aria-hidden="true" className="rx-atmenu-ic" />
              <span className="rx-atmenu-label">{it.label}</span>
              <span className="rx-atmenu-desc">{it.isCtx ? 'Anexar' : it.desc}</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
